// apps/office/src/components/office/PaymentReminderDialog.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Bell, Loader2, Send, Download, Printer, Eye } from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import { sendDocumentEmail } from '../../lib/emailService';
import { generatePdfBlob, downloadBlob } from '../../lib/pdfUtils';
import PaymentReminderPDF from './PaymentReminderPDF';

const rs = (v) =>
  `Rs. ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(v) || 0)}`;

const PaymentReminderDialog = ({
  open, onOpenChange, client, invoices = [], payments = [],
}) => {
  const validInvoices = useMemo(
    () => (invoices || []).filter((i) => (i.field || 'Draft') !== 'Cancelled'),
    [invoices]
  );

  const totalInvoiced = validInvoices.reduce(
    (s, i) => s + (Number(i.totalAmount) || Number(i.total) || 0), 0
  );
  const totalPaid = (payments || [])
    .filter((p) => (p.status || 'Completed') === 'Completed')
    .reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const totalDue = Math.max(0, totalInvoiced - totalPaid);

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTo(client?.email || '');
    setCc('');
    setSubject(`Payment Reminder — ${client?.name || ''} (Due: ${rs(totalDue)})`);
    setMessage(
      `Dear ${client?.name || 'Customer'},\n\n` +
      `This is a gentle reminder that an amount of ${rs(totalDue)} is currently ` +
      `outstanding on your account.\n\n` +
      `Please find attached a statement with the full breakdown of invoices and ` +
      `payments received so far.\n\n` +
      `If you have already made the payment, kindly share the transaction ` +
      `reference so we can update our records.\n\n` +
      `Best regards,\nHouseDesigns99 Team`
    );
  }, [open, client?.id, totalDue, client?.name, client?.email]);

  const buildPdfElement = () => (
    <PaymentReminderPDF
      client={client}
      invoices={invoices}
      payments={payments}
    />
  );

  /* ─── Download PDF ─── */
  const handleDownload = async () => {
    setGenerating(true);
    try {
      const blob = await generatePdfBlob(buildPdfElement());
      const safeName = (client?.name || 'client').replace(/\s+/g, '_');
      downloadBlob(blob, `payment_reminder_${safeName}.pdf`);
      toast.success('Reminder PDF downloaded');
    } catch (e) {
      console.error(e);
      toast.error('Failed to generate PDF: ' + e.message);
    } finally {
      setGenerating(false);
    }
  };

  /* ─── View PDF in new tab ─── */
  const handleView = async () => {
    setGenerating(true);
    try {
      const blob = await pdf(buildPdfElement()).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Revoke later (window still needs it open)
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error(e);
      toast.error('Failed to open PDF: ' + e.message);
    } finally {
      setGenerating(false);
    }
  };

  /* ─── Print PDF (same pattern as InvoiceDetail) ─── */
  const handlePrint = async () => {
    setPrinting(true);
    try {
      const blob = await pdf(buildPdfElement()).toBlob();
      const url = URL.createObjectURL(blob);

      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = url;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error('Print error:', e);
          window.open(url, '_blank');
        }
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(url);
        }, 60000);
      };
    } catch (e) {
      console.error(e);
      toast.error('Print failed: ' + e.message);
    } finally {
      setPrinting(false);
    }
  };

  /* ─── Send Email ─── */
  const handleSend = async () => {
    if (!to) { toast.error('Recipient email is required'); return; }
    setSending(true);
    try {
      let pdfBlob = null;
      try {
        pdfBlob = await generatePdfBlob(buildPdfElement());
      } catch (e) {
        console.warn('PDF gen failed, sending without attachment', e);
      }

      const result = await sendDocumentEmail({
        to, cc, subject, message,
        documentType: 'payment_reminder',
        documentNumber: client?.jobId || '',
        clientName: client?.name || '',
        pdfBlob,
      });

      if (result.method === 'mailto') {
        toast.info('Email client opened. Attach the downloaded PDF manually.');
      } else {
        toast.success('Payment reminder sent!');
      }
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to send: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-red-600" />
            Send Payment Reminder
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2 py-2">
          <div className="rounded-lg bg-slate-50 p-3">
            <p className="text-[10px] uppercase text-slate-500 tracking-wide">Total Invoiced</p>
            <p className="font-bold text-slate-800">{rs(totalInvoiced)}</p>
          </div>
          <div className="rounded-lg bg-emerald-50 p-3">
            <p className="text-[10px] uppercase text-emerald-600 tracking-wide">Total Paid</p>
            <p className="font-bold text-emerald-700">{rs(totalPaid)}</p>
          </div>
          <div className="rounded-lg bg-red-50 p-3 border border-red-200">
            <p className="text-[10px] uppercase text-red-600 tracking-wide">Amount Due</p>
            <p className="font-bold text-red-700">{rs(totalDue)}</p>
          </div>
        </div>

        <div className="space-y-3 py-1">
          <div>
            <Label>To *</Label>
            <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@example.com" />
          </div>
          <div>
            <Label>CC (optional)</Label>
            <Input type="email" value={cc} onChange={(e) => setCc(e.target.value)} />
          </div>
          <div>
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea rows={6} value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
          <p className="text-xs text-slate-500">
            Reminder PDF (invoice breakdown + payment history + amount due) will be
            auto-attached. Backend না থাকলে default email client খুলবে।
          </p>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleView}
            disabled={generating || sending || printing}
            className="gap-2"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            View
          </Button>
          <Button
            variant="outline"
            onClick={handlePrint}
            disabled={generating || sending || printing}
            className="gap-2"
          >
            {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
            Print
          </Button>
          <Button
            variant="outline"
            onClick={handleDownload}
            disabled={generating || sending || printing}
            className="gap-2"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || generating || printing}
            className="gap-2 bg-red-600 hover:bg-red-700 text-white"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? 'Sending...' : 'Send Reminder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentReminderDialog;
